import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CloudRain, Wind, Thermometer, Sun, Droplets, Zap, Eye, Gauge } from 'lucide-react';


interface WeatherDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    weatherData: any; // Ideally typed, but loose for now
}

export const WeatherDetailModal: React.FC<WeatherDetailModalProps> = ({ isOpen, onClose, weatherData }) => {
    const [aiSummary, setAiSummary] = useState<string>('');

    useEffect(() => {
        if (isOpen) {
            // Simulate generating a fresh AI summary for the weather
            // In a real app, this would call an LLM with specific weather parameters
            const prompts = [
                "Atmospheric pressure is stabilizing, but high humidity persists. Expect reduced visibility in sector 7.",
                "Solar flares are minimal today. UV index is moderate. Optimal conditions for outdoor surveillance.",
                "Wind shear is increasing from the North-East. Drone operations may experience turbulence.",
                "Thermal scans indicate a rising heat front. Hydration protocols active. Stay alert for heat stress."
            ];
            const randomPrompt = prompts[Math.floor(Math.random() * prompts.length)];
            setAiSummary(randomPrompt);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                    />

                    {/* Modal Content */}
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: 20 }}
                        className="relative w-full max-w-md bg-[#0a0a0a] border border-white/20 rounded-[32px] overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.8)]"
                    >
                        {/* Header Image / Gradient */}
                        <div className="h-32 bg-gradient-to-br from-blue-900 via-indigo-900 to-black relative">
                            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1534088568595-a066f410bcda?q=80&w=1000&auto=format&fit=crop')] bg-cover bg-center opacity-30 mix-blend-overlay"></div>
                            <button
                                onClick={onClose}
                                className="absolute top-4 right-4 p-2 bg-black/40 rounded-full hover:bg-black/60 transition-colors text-white/70 hover:text-white"
                            >
                                <X className="w-5 h-5" />
                            </button>

                            <div className="absolute bottom-4 left-6">
                                <h2 className="text-3xl font-black text-white tracking-tight">METEOROLOGY</h2>
                                <div className="flex items-center gap-2 text-blue-200/80 text-xs font-mono">
                                    <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></span>
                                    LIVE SATELLITE FEED
                                </div>
                            </div>
                        </div>

                        {/* Content Body */}
                        <div className="p-6 space-y-6">

                            {/* AI Summary Card */}
                            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[var(--neon-cyan)] to-transparent"></div>
                                <div className="flex items-start gap-3">
                                    <Zap className="w-5 h-5 text-[var(--neon-cyan)] mt-1 flex-shrink-0" />
                                    <div>
                                        <h3 className="text-[10px] uppercase font-bold text-[var(--neon-cyan)] tracking-widest mb-1">AI ANALYSIS</h3>
                                        <p className="text-sm text-gray-300 leading-relaxed font-mono">
                                            "{aiSummary}"
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Main Temp & status */}
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-5xl font-black text-white tracking-tighter">24°<span className="text-2xl text-gray-500">C</span></div>
                                    <div className="text-gray-400 text-sm font-medium">Partly Cloudy</div>
                                </div>
                                <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center border border-blue-500/20">
                                    <CloudRain className="w-8 h-8 text-blue-400" />
                                </div>
                            </div>

                            {/* Grid Stats */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-white/5 rounded-2xl p-3 flex flex-col gap-2 border border-white/5 hover:border-white/10 transition-colors">
                                    <div className="flex items-center gap-2 text-gray-500 text-xs font-bold uppercase tracking-wider">
                                        <Droplets className="w-3 h-3" /> Humidity
                                    </div>
                                    <span className="text-xl font-bold text-white">65%</span>
                                </div>
                                <div className="bg-white/5 rounded-2xl p-3 flex flex-col gap-2 border border-white/5 hover:border-white/10 transition-colors">
                                    <div className="flex items-center gap-2 text-gray-500 text-xs font-bold uppercase tracking-wider">
                                        <Wind className="w-3 h-3" /> Wind
                                    </div>
                                    <span className="text-xl font-bold text-white">12 <span className="text-xs text-gray-500">km/h</span></span>
                                </div>
                                <div className="bg-white/5 rounded-2xl p-3 flex flex-col gap-2 border border-white/5 hover:border-white/10 transition-colors">
                                    <div className="flex items-center gap-2 text-gray-500 text-xs font-bold uppercase tracking-wider">
                                        <Sun className="w-3 h-3" /> UV Index
                                    </div>
                                    <span className="text-xl font-bold text-white">Moderate</span>
                                </div>
                                <div className="bg-white/5 rounded-2xl p-3 flex flex-col gap-2 border border-white/5 hover:border-white/10 transition-colors">
                                    <div className="flex items-center gap-2 text-gray-500 text-xs font-bold uppercase tracking-wider">
                                        <Eye className="w-3 h-3" /> Visibility
                                    </div>
                                    <span className="text-xl font-bold text-white">10 <span className="text-xs text-gray-500">km</span></span>
                                </div>
                            </div>

                            {/* Hourly Forecast (Micro) */}
                            <div>
                                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Forecast</h4>
                                <div className="flex justify-between">
                                    {[1, 2, 3, 4, 5].map((_, i) => (
                                        <div key={i} className="flex flex-col items-center gap-2">
                                            <span className="text-[10px] text-gray-400">{(12 + i) % 12 || 12} PM</span>
                                            <CloudRain className="w-4 h-4 text-gray-600" />
                                            <span className="text-xs font-bold text-white">2{i + 2}°</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};
